import React from 'react';
import { Table, Space, Button, Tooltip, Popconfirm } from 'antd';
import { EditOutlined, DeleteOutlined, EyeOutlined, MoreOutlined } from '@ant-design/icons';
import type { ColumnsType, TableProps } from 'antd/es/table';
import type { TableRowSelection } from 'antd/lib/table/interface';
import { TableAction } from '../../types';

interface LibraryTableProps<T> extends Omit<TableProps<T>, 'columns'> {
  data: T[];
  columns: ColumnsType<T>;
  onEdit?: (record: T) => void;
  onDelete?: (record: T) => void;
  onView?: (record: T) => void;
  additionalActions?: TableAction[];
  showActions?: boolean;
  actionsWidth?: number;
  selectedRowKeys?: React.Key[];
  onSelectionChange?: (selectedRowKeys: React.Key[], selectedRows: T[]) => void;
  selectable?: boolean;
}

const LibraryTable = <T extends { id: string }>({
  data,
  columns,
  onEdit,
  onDelete,
  onView,
  additionalActions = [],
  showActions = true,
  actionsWidth = 120,
  selectedRowKeys = [],
  onSelectionChange,
  selectable = true,
  ...tableProps
}: LibraryTableProps<T>) => {
  // Create action column
  const actionColumn: ColumnsType<T>[0] = {
    title: 'Actions',
    key: 'actions',
    width: actionsWidth,
    fixed: 'right',
    render: (_, record) => (
      <Space size="small">
        {onView && (
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => onView(record)}
              size="small"
            />
          </Tooltip>
        )}
        
        {onEdit && (
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => onEdit(record)}
              size="small"
            />
          </Tooltip>
        )}
        
        {onDelete && (
          <Popconfirm
            title="Are you sure you want to delete this item?"
            onConfirm={() => onDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button
                type="text"
                icon={<DeleteOutlined />}
                danger
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        )}
        
        {additionalActions.map((action) => (
          <Tooltip key={action.key} title={action.label}>
            <Button
              type="text"
              icon={action.icon || <MoreOutlined />}
              onClick={() => action.onClick(record)}
              danger={action.danger}
              size="small"
            />
          </Tooltip>
        ))}
      </Space>
    ),
  };

  // Combine columns with action column
  const finalColumns = showActions 
    ? [...columns, actionColumn]
    : columns;

  // Row selection configuration
  const rowSelection: TableRowSelection<T> | undefined = selectable && onSelectionChange ? {
    selectedRowKeys,
    onChange: onSelectionChange,
    preserveSelectedRowKeys: true,
  } : undefined;

  return (
    <Table<T>
      {...tableProps}
      columns={finalColumns}
      dataSource={data}
      rowKey="id"
      rowSelection={rowSelection}
      pagination={{
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) => 
          `${range[0]}-${range[1]} of ${total} items`,
        pageSizeOptions: ['10', '20', '50', '100'],
        ...tableProps.pagination,
      }}
      scroll={{ x: 'max-content' }}
      size="small"
    />
  );
};

export default LibraryTable; 